import React, { useState } from 'react';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Square } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ConfirmNoteDialog from '@/components/ConfirmNoteDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RecordButton() {
  const { isRecording, audioBlob, startRecording, stopRecording, resetRecording } = useAudioRecorder();
  const [processing, setProcessing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleRecordClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  React.useEffect(() => {
    if (audioBlob && !isRecording) {
      processAudio();
    }
  }, [audioBlob, isRecording]);

  const processAudio = async () => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const transcribeResponse = await axios.post(
        `${API_URL}/notes/transcribe`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const transcriptionText = transcribeResponse.data.transcription;
      setTranscription(transcriptionText);
      setShowConfirmDialog(true);
    } catch (error) {
      console.error('Errore elaborazione audio:', error);
      toast.error('Errore nell\'elaborazione dell\'audio');
    } finally {
      setProcessing(false);
    }
  };

  const handleDialogClose = () => {
    setShowConfirmDialog(false);
    setTranscription('');
    resetRecording();
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center space-y-6">
        <AnimatePresence mode="wait">
          {processing ? (
            <motion.div
              key="processing"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="text-center"
            >
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-indigo-50 border-2 border-indigo-200 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">Trascrizione in corso...</p>
            </motion.div>
          ) : (
            <motion.button
              key="record-button"
              onClick={handleRecordClick}
              className={`w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center transition-all duration-300 active:scale-90 shadow-2xl z-50 ${
                isRecording
                  ? 'bg-red-500 animate-recording-pulse'
                  : 'bg-primary hover:bg-primary/90 animate-pulse-glow'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="record-button"
            >
              {isRecording ? (
                <Square className="w-12 h-12 md:w-16 md:h-16 text-white" />
              ) : (
                <Mic className="w-12 h-12 md:w-16 md:h-16 text-white" />
              )}
            </motion.button>
          )}
        </AnimatePresence>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-sm md:text-base text-muted-foreground font-medium"
        >
          {processing
            ? 'Sto trascrivendo...'
            : isRecording
            ? 'Registrazione in corso...'
            : 'Tocca per registrare'}
        </motion.p>
      </div>

      {showConfirmDialog && (
        <ConfirmNoteDialog
          open={showConfirmDialog}
          onClose={handleDialogClose}
          transcription={transcription}
        />
      )}
    </>
  );
}